import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { CheckCircle, XCircle, Loader2, Crown, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token, refreshUser, isAuthenticated } = useAuth();
  
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("loading"); // loading, success, failed
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      navigate("/membership");
      return;
    }

    if (!isAuthenticated) {
      navigate("/login?redirect=/membership");
      return;
    }

    pollPaymentStatus();
  }, [sessionId, isAuthenticated]);

  const pollPaymentStatus = async () => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus("failed");
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.payment_status === "paid") {
        setStatus("success");
        // Refresh user data to get updated membership
        await refreshUser();
        return;
      } else if (response.data.status === "expired") {
        setStatus("failed");
        return;
      }

      // Continue polling
      setAttempts((prev) => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    } catch (error) {
      console.error("Payment status check failed:", error);
      setAttempts((prev) => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4" data-testid="payment-success-page">
      <div className="max-w-md w-full">
        {status === "loading" && (
          <div className="text-center bg-zinc-900 border border-zinc-800 rounded-sm p-8">
            <Loader2 className="h-12 w-12 text-cyan-400 animate-spin mx-auto mb-4" />
            <h1 className="text-xl font-bold text-white mb-2">Zahlung wird überprüft...</h1>
            <p className="text-zinc-500 text-sm">
              Bitte warten Sie, während wir Ihre Zahlung bestätigen.
            </p>
          </div>
        )}

        {status === "success" && (
          <div className="text-center bg-zinc-900 border border-zinc-800 rounded-sm p-8">
            <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-8 w-8 text-green-400" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2 font-['Chivo']">
              Willkommen im Premium-Club!
            </h1>
            <p className="text-zinc-400 mb-6">
              Ihre Zahlung war erfolgreich. Sie sind jetzt Premium-Mitglied und 
              genießen alle exklusiven Vorteile.
            </p>
            
            <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-sm p-4 mb-6">
              <div className="flex items-center justify-center gap-2 text-cyan-400">
                <Crown className="h-5 w-5" />
                <span className="font-semibold">Premium-Mitglied</span>
              </div>
              <p className="text-sm text-zinc-500 mt-2">
                15% Rabatt auf alle Services aktiv
              </p>
            </div>

            <div className="space-y-3">
              <Link to="/booking" className="block">
                <Button className="w-full bg-cyan-500 hover:bg-cyan-400 text-black font-semibold btn-glow" data-testid="book-after-payment-btn">
                  Jetzt mit Rabatt buchen
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/dashboard" className="block">
                <Button variant="outline" className="w-full border-zinc-700" data-testid="dashboard-after-payment-btn">
                  Zum Dashboard
                </Button>
              </Link>
            </div>
          </div>
        )}

        {status === "failed" && (
          <div className="text-center bg-zinc-900 border border-zinc-800 rounded-sm p-8">
            <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="h-8 w-8 text-red-400" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2 font-['Chivo']">
              Zahlung fehlgeschlagen
            </h1>
            <p className="text-zinc-400 mb-6">
              Leider konnte Ihre Zahlung nicht verarbeitet werden. 
              Bitte versuchen Sie es erneut.
            </p>

            <div className="space-y-3">
              <Link to="/membership" className="block">
                <Button className="w-full bg-cyan-500 hover:bg-cyan-400 text-black font-semibold" data-testid="retry-payment-btn">
                  Erneut versuchen
                </Button>
              </Link>
              <Link to="/" className="block">
                <Button variant="outline" className="w-full border-zinc-700">
                  Zur Startseite
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
